(function () {
  'use strict';

  if (window.__qcWriteWithUsInit) return;
  window.__qcWriteWithUsInit = true;

  const productContext = window.qcProductContext || {};
  const WORKER_URL = window.qcWorkerUrl || '';
  const CLIPBOARD_KEY = 'qcMessageClipboard';
  const ATTR_KEY = 'Saved Card Messages';
  const QC_EVENT_NS = 'write_with_us:';

  const state = {
    occasion: '',
    recipient: '',
    tone: 'Heartfelt',
    length: 'Short',
    notes: '',
    results: [],
    selected: [],
    justRefreshed: [],
    clipboardOpen: false,
    statusMsg: '',
    // Never put failures in `results`: those are selectable and get saved to
    // the cart attribute and the order confirmation email.
    errorMsg: '',
    loading: false,
  };

  const OCCASION_TAGS = [
    'Birthday',
    'Anniversary',
    'Thank You',
    'Congratulations',
    'Sympathy',
    'Wedding',
    'New Baby',
  ];

  // Publishes to Shopify's event bus rather than calling GA4 directly: gtag
  // lives in the Web Pixels sandbox, out of reach from theme code. The "Write
  // with Us — GA4" custom pixel forwards these; QC_EVENT_NS must match the
  // EVENT_PREFIX it subscribes with.
  function track(eventName, params) {
    const payload = params || {};
    try {
      window.Shopify?.analytics?.publish?.(QC_EVENT_NS + eventName, payload);
    } catch (e) {
      /* non-fatal */
    }
    try {
      document.dispatchEvent(
        new CustomEvent('qc:track', { detail: { name: eventName, payload } })
      );
    } catch (e) {
      /* non-fatal */
    }
  }

  const PENCIL_ICON =
    '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M20.71 7.04c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.37-.39-1.02-.39-1.41 0l-1.84 1.83 3.75 3.75M3 17.25V21h3.75L17.81 9.93l-3.75-3.75L3 17.25Z"/></svg>';

  const HOW_IT_WORKS =
    'Tell us who this is for, select your favorites, then tap <strong>Save to Clipboard</strong>. Saved messages will be automatically included in your order confirmation.';

  function escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function cartUrl(path) {
    const routes = window.theme?.routes || {};
    if (path === 'add') {
      return routes.cart_add_url || '/cart/add.js';
    }
    return routes.cart_update_url
      ? `${routes.cart_update_url}.js`
      : '/cart/update.js';
  }

  // Scoped to this section's form id: a document-wide [name="id"] lookup can
  // match a quick-add form in recommendations or the cart drawer. Uses
  // elements.namedItem because the quantity input sits outside the <form> and
  // is associated by its form attribute.
  function productFormControl(name) {
    const form = productContext.formId
      ? document.getElementById(productContext.formId)
      : null;
    return form?.elements?.namedItem(name) || null;
  }

  function currentVariantId() {
    const input = productFormControl('id');
    const fromForm = input && input.value ? Number(input.value) : NaN;
    return Number.isFinite(fromForm) ? fromForm : productContext.variantId;
  }

  function currentQuantity() {
    const input = productFormControl('quantity');
    const qty = input && input.value ? Number(input.value) : NaN;
    return Number.isFinite(qty) && qty > 0 ? Math.floor(qty) : 1;
  }

  const tags = Array.isArray(productContext.tags) ? productContext.tags : [];
  const occasionMatch = OCCASION_TAGS.find((o) =>
    tags.some((t) => String(t).toLowerCase() === o.toLowerCase())
  );
  if (occasionMatch) state.occasion = occasionMatch;

  function loadClipboard() {
    try {
      const parsed = JSON.parse(localStorage.getItem(CLIPBOARD_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  // localStorage + cart attribute stay in lockstep. Attribute format is
  // "Card|||Message|||Image|||URL@@@..." so order-confirmation Liquid can
  // split it — Shopify email Liquid has no JSON parse filter.
  async function persistClipboard(arr) {
    try {
      localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(arr));
    } catch (e) {
      /* ignore quota errors */
    }
    document.documentElement.dispatchEvent(
      new CustomEvent('qc:clipboard-updated', { bubbles: true })
    );
    try {
      await fetch(cartUrl('update'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            [ATTR_KEY]: arr.length
              ? arr
                  .map(
                    (item) =>
                      `${item.card}|||${item.text}|||${item.image || ''}|||${item.url || ''}`
                  )
                  .join('@@@')
              : '',
          },
        }),
      });
    } catch (e) {
      /* non-fatal */
    }
  }

  // Keep the drawer above the sticky site header by mounting it on <body>
  (function mountPanelToBody() {
    const overlay = document.getElementById('wmOverlay');
    const panel = document.getElementById('wmPanel');
    if (!overlay || !panel) return;
    if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
    if (panel.parentElement !== document.body) {
      document.body.appendChild(panel);
    }
  })();

  function clipboardBarAndPanel() {
    const arr = loadClipboard();
    const bar = `
      <div class="qc-clipboard-bar" data-qc-toggle-clipboard>
        <span>🗂 Saved messages</span>
        <span class="count">${arr.length}</span>
      </div>`;
    if (!state.clipboardOpen) return bar;

    const panel = arr.length
      ? `<div class="qc-clipboard-panel">${arr
          .map(
            (item, i) => `
          <div class="qc-clipboard-item">
            <div class="qc-clipboard-item-text">${escapeHtml(item.text)}</div>
            <button type="button" class="qc-clipboard-remove" data-qc-remove-clipboard="${i}">Remove</button>
          </div>
        `
          )
          .join('')}</div>`
      : `<div class="qc-clipboard-panel"><div class="qc-clipboard-empty">No saved messages yet — select one below and tap "Save to Clipboard & Add to Cart".</div></div>`;

    return bar + panel;
  }

  function errorBlock() {
    return state.errorMsg
      ? `<div class="qc-error-msg" role="alert">${escapeHtml(state.errorMsg)}</div>`
      : '';
  }

  function renderPanel() {
    const body = document.getElementById('wmBody');
    if (!body) return;

    let belowForm = '';

    if (state.loading) {
      belowForm = `
        <div class="qc-results-section" id="qcResultsSection">
          <div class="qc-loading">Writing something worth keeping...</div>
        </div>`;
    } else if (state.results.length > 0) {
      const allLocked = state.results.every((_, i) => state.selected[i]);
      belowForm = `
        <div class="qc-results-section" id="qcResultsSection">
          ${clipboardBarAndPanel()}
          ${errorBlock()}
          ${state.results
            .map(
              (r, i) => `
            <div class="qc-result ${state.selected[i] ? 'locked' : ''}">
              <input type="checkbox" class="qc-result-check" ${
                state.selected[i] ? 'checked' : ''
              } data-qc-select="${i}" aria-label="Keep this message">
              <div class="qc-result-main">
                ${
                  state.selected[i]
                    ? '<span class="qc-locked-tag">Kept for next round</span>'
                    : ''
                }
                ${
                  state.justRefreshed[i]
                    ? '<span class="qc-refreshed-tag">Refreshed</span>'
                    : ''
                }
                <div class="qc-result-text">${escapeHtml(r)}</div>
              </div>
            </div>
          `
            )
            .join('')}
          <button type="button" class="qc-regen-btn" data-qc-regenerate ${
            allLocked ? 'disabled' : ''
          } title="Regenerate unselected messages">Refresh Messages</button>
          <button type="button" class="qc-btn" data-qc-save-cart>Save to Clipboard &amp; Add to Cart</button>
          ${
            state.statusMsg
              ? `<div class="qc-status-msg">${escapeHtml(state.statusMsg)}</div>`
              : ''
          }
        </div>`;
    } else if (state.errorMsg) {
      belowForm = `
        <div class="qc-results-section" id="qcResultsSection">
          ${errorBlock()}
        </div>`;
    }

    body.innerHTML = `
      <div class="qc-how-it-works">
        ${HOW_IT_WORKS}
      </div>

      <div class="qc-section">
        <label class="qc-label" for="qcOccasion">Occasion</label>
        <input class="qc-input" id="qcOccasion" placeholder="Birthday, anniversary, thank you..." value="${escapeHtml(state.occasion)}">

        <label class="qc-label" for="qcRecipient">Who's it for?</label>
        <input class="qc-input" id="qcRecipient" placeholder="My mom, a close friend, my boss..." value="${escapeHtml(state.recipient)}">

        <label class="qc-label" for="qcTone">Tone</label>
        <select class="qc-select" id="qcTone">
          ${['Heartfelt', 'Playful & funny', 'Elegant & poetic', 'Simple & warm']
            .map(
              (t) =>
                `<option ${t === state.tone ? 'selected' : ''}>${escapeHtml(t)}</option>`
            )
            .join('')}
        </select>

        <label class="qc-label">Message length</label>
        <div class="qc-toggle-row">
          <button type="button" class="qc-toggle-btn ${state.length === 'Short' ? 'active' : ''}" data-qc-length="Short">Short</button>
          <button type="button" class="qc-toggle-btn ${state.length === 'Long' ? 'active' : ''}" data-qc-length="Long">Long</button>
        </div>

        <label class="qc-label" for="qcNotes">Tell me something special (optional)</label>
        <textarea class="qc-textarea" id="qcNotes" placeholder="An inside joke, a memory, why you're grateful...">${escapeHtml(state.notes)}</textarea>
      </div>

      <button type="button" class="qc-btn" data-qc-generate>${PENCIL_ICON} Write My Message</button>

      ${belowForm}
    `;
  }

  function syncFormFields() {
    // Read values directly — do not use || fallbacks. An empty string is a
    // deliberate clear; treating it as falsy would re-fill the field from
    // the previous state on the next render (e.g. after generate).
    const occasionEl = document.getElementById('qcOccasion');
    const recipientEl = document.getElementById('qcRecipient');
    const toneEl = document.getElementById('qcTone');
    const notesEl = document.getElementById('qcNotes');
    if (occasionEl) state.occasion = occasionEl.value;
    if (recipientEl) state.recipient = recipientEl.value;
    if (toneEl) state.tone = toneEl.value;
    if (notesEl) state.notes = notesEl.value;
  }

  function scrollToResultsSection() {
    document
      .getElementById('qcResultsSection')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Both timings track animations in write-with-us.css — change them together.
  // 2300ms outlasts the checkmark + text animations, 350ms the panel close.
  function showSuccessAndClose() {
    const overlay = document.getElementById('qcSuccessOverlay');
    if (!overlay) return;
    overlay.classList.add('show');
    setTimeout(() => {
      closePanel();
      setTimeout(() => overlay.classList.remove('show'), 350);
    }, 2300);
  }

  async function saveAndAddToCart() {
    const selectedMessages = state.results.filter((_, i) => state.selected[i]);

    if (selectedMessages.length === 0) {
      state.statusMsg =
        'Select at least one message first (check the box next to it).';
      renderPanel();
      return;
    }

    // Deduped on card + text: the same wording saved against two cards is two
    // legitimate entries.
    const arr = loadClipboard();
    selectedMessages.forEach((text) => {
      if (
        !arr.some(
          (item) => item.text === text && item.card === productContext.title
        )
      ) {
        arr.push({
          text,
          card: productContext.title,
          image: productContext.image || '',
          url: productContext.url || '',
          savedAt: Date.now(),
        });
      }
    });

    // Persist before add-to-cart, so confirmation emails still get the messages
    // if the add fails.
    await persistClipboard(arr);

    state.statusMsg = '';
    renderPanel();

    const variantId = currentVariantId();
    if (!variantId) {
      state.statusMsg = "Couldn't add to cart — saved to clipboard only.";
      renderPanel();
      return;
    }

    let succeeded = false;

    try {
      const res = await fetch(cartUrl('add'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: currentQuantity() }],
        }),
      });

      if (res.ok) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );
        succeeded = true;
      } else {
        state.statusMsg = "Couldn't add to cart — saved to clipboard only.";
      }
    } catch (e) {
      state.statusMsg = "Couldn't add to cart — saved to clipboard only.";
    }

    if (succeeded) {
      track('message_saved_to_cart', {
        card_title: productContext.title,
        messages_saved: selectedMessages.length,
        messages_in_clipboard: loadClipboard().length,
      });
      showSuccessAndClose();
    } else {
      renderPanel();
    }
  }

  // After a failed first write there are no results, so no Refresh button yet.
  function writeFailedMessage() {
    const button = state.results.length
      ? 'Refresh Messages'
      : 'Write My Message';
    return `We couldn't write that just now — tap ${button} to try again.`;
  }

  async function generate(isRegenerate) {
    state.statusMsg = '';
    state.errorMsg = '';
    syncFormFields();

    // Only "Refresh Messages" keeps checked messages; a fresh write starts over.
    if (!isRegenerate) {
      state.selected = [];
      state.results = [];
      state.justRefreshed = [];
    }

    const lockedResults = state.results.filter((_, i) => state.selected[i]);
    const neededCount = Math.max(0, 3 - lockedResults.length);

    // Everything is checked, so a refresh has nothing to replace.
    if (isRegenerate && neededCount === 0) return;

    const eventName = isRegenerate ? 'regenerate_clicked' : 'message_generated';
    function trackOutcome(outcome) {
      track(eventName, {
        card_title: productContext.title,
        occasion: state.occasion,
        tone: state.tone,
        length: state.length,
        outcome,
      });
    }

    if (!WORKER_URL) {
      state.errorMsg =
        'Writing assistant is not configured yet. Please try again later.';
      state.loading = false;
      renderPanel();
      scrollToResultsSection();
      trackOutcome('not_configured');
      return;
    }

    state.loading = true;
    renderPanel();
    scrollToResultsSection();

    let outcome = 'success';

    try {
      const response = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          occasion: state.occasion,
          recipient: state.recipient,
          tone: state.tone,
          notes: state.notes,
          length: state.length,
          productContext: {
            title: productContext.title,
            description: productContext.description,
            tags: productContext.tags,
          },
        }),
      });

      if (response.status === 429) {
        state.errorMsg =
          "You've reached the limit for now — please try again in a little while.";
        outcome = 'rate_limited';
      } else if (response.status === 422) {
        // Content policy block or generation refusal — the Worker already
        // crafted a specific, customer-safe message for this case (see
        // callModerationClassifier / checkAllFieldsLocally on the backend).
        // Read it instead of falling into the generic error bucket below,
        // so the customer knows it's their input, not a bug on our end.
        let blockedMsg =
          'This message could not be generated because it does not meet our content guidelines. Please revise and try again.';
        try {
          const errData = await response.json();
          if (errData && typeof errData.error === 'string' && errData.error)
            blockedMsg = errData.error;
        } catch (e) {
          /* keep the fallback message above */
        }
        state.errorMsg = blockedMsg;
        outcome = 'blocked';
      } else if (!response.ok) {
        state.errorMsg = writeFailedMessage();
        outcome = 'error';
      } else {
        const data = await response.json();
        const fresh =
          Array.isArray(data.messages) && data.messages.length
            ? data.messages
            : [];

        if (fresh.length) {
          if (state.results.length === 0) {
            state.results = fresh.slice(0, 3);
            state.selected = state.results.map(() => false);
            state.justRefreshed = state.results.map(() => false);
          } else {
            let freshIndex = 0;
            const refreshedFlags = [];
            state.results = state.results.map((r, i) => {
              if (state.selected[i]) {
                refreshedFlags[i] = false;
                return r;
              }
              const hasReplacement = fresh[freshIndex] !== undefined;
              const replacement = hasReplacement ? fresh[freshIndex] : r;
              refreshedFlags[i] = hasReplacement;
              freshIndex++;
              return replacement;
            });
            state.justRefreshed = refreshedFlags;
          }
        } else {
          state.errorMsg = writeFailedMessage();
          outcome = 'empty';
        }
      }
    } catch (e) {
      state.errorMsg =
        "We couldn't write that just now — please try again shortly.";
      outcome = 'error';
    }

    state.loading = false;
    renderPanel();
    trackOutcome(outcome);
    scrollToResultsSection();
  }

  function openPanel() {
    const overlay = document.getElementById('wmOverlay');
    const panel = document.getElementById('wmPanel');
    if (!overlay || !panel) return;

    overlay.classList.add('open');
    panel.classList.add('open');
    document.documentElement.classList.add('overflow-hidden');
    track('panel_opened', { card_title: productContext.title });
    renderPanel();
  }

  function closePanel() {
    document.getElementById('wmOverlay')?.classList.remove('open');
    document.getElementById('wmPanel')?.classList.remove('open');
    document.documentElement.classList.remove('overflow-hidden');
  }

  document.addEventListener('click', (event) => {
    const openBtn = event.target.closest('[data-qc-open]');
    if (openBtn) {
      event.preventDefault();
      openPanel();
      return;
    }

    const closeBtn = event.target.closest('[data-qc-close]');
    if (closeBtn) {
      event.preventDefault();
      closePanel();
      return;
    }

    if (event.target.closest('[data-qc-regenerate]')) {
      event.preventDefault();
      generate(true);
      return;
    }

    if (event.target.closest('[data-qc-generate]')) {
      event.preventDefault();
      generate(false);
      return;
    }

    if (event.target.closest('[data-qc-save-cart]')) {
      event.preventDefault();
      saveAndAddToCart();
      return;
    }

    const lengthBtn = event.target.closest('[data-qc-length]');
    if (lengthBtn) {
      event.preventDefault();
      syncFormFields();
      state.length = lengthBtn.getAttribute('data-qc-length');
      renderPanel();
      return;
    }

    if (event.target.closest('[data-qc-toggle-clipboard]')) {
      event.preventDefault();
      state.clipboardOpen = !state.clipboardOpen;
      renderPanel();
      return;
    }

    const removeBtn = event.target.closest('[data-qc-remove-clipboard]');
    if (removeBtn) {
      event.preventDefault();
      const idx = Number(removeBtn.getAttribute('data-qc-remove-clipboard'));
      const arr = loadClipboard();
      if (!Number.isNaN(idx) && idx >= 0 && idx < arr.length) {
        arr.splice(idx, 1);
        persistClipboard(arr);
      }
      renderPanel();
      return;
    }

    const selectEl = event.target.closest('[data-qc-select]');
    if (selectEl) {
      const i = Number(selectEl.getAttribute('data-qc-select'));
      if (!Number.isNaN(i)) {
        state.selected[i] = !state.selected[i];
        if (state.selected[i]) {
          track('message_selected', { card_title: productContext.title });
        }
        renderPanel();
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePanel();
  });
})();
